import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { RoleGuard } from '@/components/auth/RoleGuard';
import { RecoveryGuard } from '@/components/auth/RecoveryGuard';
import { AuthPage } from '@/pages/AuthPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { AdminResetLinkTester } from '@/pages/AdminResetLinkTester';
import { SecurityLevelPicker } from '@/pages/SecurityLevelPicker';
import { SecuritySettingsPage } from '@/pages/SecuritySettingsPage';
import IndexPage from '@/pages/Index';
import { AdminDashboard as AdminDashboardPage } from '@/pages/AdminDashboard';
import { StaffDashboard as StaffDashboardPage } from '@/pages/StaffDashboard';
import { MobileAppShell } from '@/components/mobile/MobileAppShell';
import { RoleManagement } from '@/components/admin/RoleManagement';
import OrderCheckInPage from '@/pages/OrderCheckInPage';
import { Orders } from '@/pages/Orders';
import { ArchivedOrdersPage } from '@/pages/ArchivedOrdersPage';
import ManageOrderV2 from '@/pages/ManageOrderV2';
import Workstation from '@/pages/Workstation';
import { StaffOrderProductsView } from '@/components/staff/StaffOrderProductsView';
import { StaffClockInPage } from '@/pages/StaffClockInPage';
import { StaffAllScannedItems } from '@/pages/StaffAllScannedItems';
import { UserManagement } from '@/components/employee/user-management';
import { HRAdminPage } from '@/pages/HRAdminPage';
import SmsLoginPage from '@/pages/SmsLoginPage';


// Create redirect components to avoid Navigate import issues
const RedirectToStaffDashboard = () => {
  React.useEffect(() => {
    window.location.replace('/staff-dashboard');
  }, []);
  return null;
};

const queryClient = new QueryClient();

// Simple fallback component for missing pages
const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
      <p className="text-muted-foreground">The requested page could not be found.</p>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RecoveryGuard>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<SmsLoginPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Security Level Picker - Admin Only */}
              <Route 
                path="/security-level-picker" 
                element={
                  <RoleGuard requireAdmin>
                    <SecurityLevelPicker />
                  </RoleGuard>
                } 
              />

              {/* Admin Tools - Protected Routes */}
              <Route 
                path="/admin/reset-link-tester" 
                element={
                  <RoleGuard requireAdmin>
                    <AdminResetLinkTester />
                  </RoleGuard>
                } 
              />

              <Route 
                path="/admin/security-settings" 
                element={
                  <RoleGuard requireAdmin>
                    <SecuritySettingsPage />
                  </RoleGuard>
                } 
              />

              <Route 
                path="/admin/roles" 
                element={
                  <RoleGuard requireAdmin>
                    <MobileAppShell>
                      <RoleManagement />
                    </MobileAppShell>
                  </RoleGuard>
                } 
              />

              {/* HR Management Routes - Admin/Manager Access */}
              <Route 
                path="/hr/user-management" 
                element={
                  <RoleGuard requiredRole="manager">
                    <MobileAppShell>
                      <UserManagement />
                    </MobileAppShell>
                  </RoleGuard>
                } 
              />

              <Route 
                path="/hr/admin" 
                element={
                  <RoleGuard requireAdmin>
                    <MobileAppShell>
                      <HRAdminPage />
                    </MobileAppShell>
                  </RoleGuard>
                } 
              />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <RoleGuard>
                    <IndexPage />
                  </RoleGuard>
                }
              />

              <Route
                path="/admin-dashboard"
                element={
                  <RoleGuard requireAdmin>
                    <MobileAppShell>
                      <AdminDashboardPage />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              <Route
                path="/staff-dashboard"
                element={
                  <RoleGuard>
                    <MobileAppShell>
                      <StaffDashboardPage />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />
              
              <Route
                path="/staff/clock-in"
                element={
                  <RoleGuard requiredRole="staff" fallbackPath="/staff-dashboard">
                    <MobileAppShell>
                      <StaffClockInPage />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />
              
              <Route
                path="/staff/scanned-items"
                element={
                  <RoleGuard requiredRole="staff" fallbackPath="/staff-dashboard">
                    <MobileAppShell>
                      <StaffAllScannedItems />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              {/* Order Management Routes */}
              <Route
                path="/checkin"
                element={
                  <RoleGuard>
                    <MobileAppShell>
                      <OrderCheckInPage />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              <Route
                path="/orders"
                element={
                  <RoleGuard>
                    <MobileAppShell>
                      <Orders />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              <Route
                path="/archived-orders"
                element={
                  <RoleGuard requireAdmin>
                    <MobileAppShell>
                      <ArchivedOrdersPage />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              <Route
                path="/orders/:id/manage"
                element={
                  <RoleGuard>
                    <MobileAppShell>
                      <ManageOrderV2 />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              <Route
                path="/orders/:orderId/products"
                element={
                  <RoleGuard requiredRole="staff">
                    <MobileAppShell>
                      <StaffOrderProductsView />
                    </MobileAppShell>
                  </RoleGuard>
                }
              />

              <Route
                path="/order-workstation/:orderId"
                element={
                  <RoleGuard requireAdmin>
                    <Workstation />
                  </RoleGuard>
                }
              />

              {/* Legacy route redirects */}
              <Route path="/dashboard" element={<RedirectToStaffDashboard />} />
              <Route path="/staff" element={<RedirectToStaffDashboard />} />
              <Route path="/home" element={<RedirectToStaffDashboard />} />

              {/* Catch-all route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </RecoveryGuard>
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
